import { createSignal, onMount, Show } from "solid-js"

const CODE_LENGTH = 6;

export interface CodeInputProps {
    onChange: (newValue: string) => void
    error: string | null
    submitting: boolean
}

const CodeInput = (props: CodeInputProps) => {
    const [codeDigits, setCodeDigits] = createSignal<string[]>(Array(CODE_LENGTH).fill(''));
    const [codeDigitRefs, setCodeDigitRefs] = createSignal<(HTMLInputElement | null)[]>(Array(CODE_LENGTH).fill(null));

    const onDigitEntered = (index: number, e: InputEvent) => {
        // If backspace is pressed and the current input is empty, focus the previous input
        if (e.inputType === 'deleteContentBackward' && index > 0) {
            // Clear the current input
            setCodeDigits((prev) => {
                const next = [...prev];
                next[index - 1] = '';
                next[index] = '';
                return next;
            });

            // Focus the previous input
            const prevInput = codeDigitRefs()[index - 1];
            if (prevInput) {
                prevInput.focus();
            }

            return;
        }

        // Only allow numeric input
        if (!isNaN(parseInt(e.data || '')) && e.data !== null) {
            setCodeDigits((prev) => {
                const next = [...prev];
                next[index] = e.data || '';
                return next;
            });

            // Focus the next input if it exists
            if (index < CODE_LENGTH - 1) {
                const nextInput = codeDigitRefs()[index + 1];
                if (nextInput) {
                    nextInput.focus();
                }
            }
        }

        // If this is the last digit, trigger the onChange callback with the full code
        const fullCode = codeDigits().join('');
        if (fullCode.length === CODE_LENGTH) {
            props.onChange(fullCode);
        }
    }

    onMount(() => {
        const firstInput = codeDigitRefs()[0];
        if (firstInput) {
            firstInput.focus();
        }
    });

    return <div class="h-full w-full flex items-center justify-center p-4">
        <div class="max-w-md rounded-lg bg-zinc-900 p-6 text-center">
            <div class="text-lg font-semibold text-white">Enter Venue Code</div>
            <div class="mt-2 text-sm text-gray-300">
                Enter the 6-digit venue code from your QR code or venue link to continue.
            </div>
            <div class="mt-5 flex items-center justify-center gap-2">
                {codeDigits().map((value, index) => (
                    <input
                        ref={(el) => {
                            setCodeDigitRefs((prev) => {
                                const next = [...prev];
                                next[index] = el;
                                return next;
                            });
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxlength={1}
                        value={value}
                        class="w-10 h-10 rounded bg-zinc-800 text-center text-white outline-none ring-2 ring-transparent focus:ring-indigo-500"
                        onInput={(e) => onDigitEntered(index, e)}
                    />
                ))}
            </div>
            <div class="mt-4 flex flex-col items-center gap-2">
                <Show when={props.submitting}>
                    <p class="text-sm text-gray-300">Checking code…</p>
                </Show>
                <Show when={props.error}>
                    <p class="text-sm text-red-400">{props.error}</p>
                </Show>
            </div>
        </div>
    </div>
}

export default CodeInput;